<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { lockBodyScroll, unlockBodyScroll } from '~/utilities/ui-utilities';
import { useAccountOptions } from '~/composables/useAccountOptions';
import Button from '~/components/design-system/button.vue';
import ExternalIcon from '~/components/design-system/icons/ExternalIcon.vue';
import { Bars3BottomLeftIcon } from '@heroicons/vue/24/outline';
import Drawer from '~/components/design-system/drawer.vue';
import HamburgerMenuHeader from './hamburger-menu-header.vue';
import HamburgerShortcuts from './hamburger-shortcuts.vue';
import GenericLanguageSwitcher from '../generic-language-switcher.vue';
import HamburgerGameCategories from './hamburger-game-categories.vue';
import type { AccountModals } from '~/interfaces/dto/general/modals';
import type { TransitionProps } from 'vue';
import type { IHamburgerMenuState } from '~/interfaces/components/layout';

const props = defineProps<{
  drawerTransition?: Partial<TransitionProps>;
  hideActivator?: boolean;
}>();

const { $walletService } = useServices();
const { $trackEvent } = useNuxtApp();
const auth = useAuthStore();
const settings = useSiteStore();
const router = useRouter();
const route = useRoute();
const viewport = useViewport();

const hamburgerMenuState = inject<IHamburgerMenuState>('hamburgerMenuState');

const accountNavBarMenu = computed(() =>
  useAccountOptions().filter((uao) => uao.includeInHamburger && uao.active),
);

const activeRoute = computed(() => router.currentRoute.value.path);

const refreshBalances = async () => {
  await $walletService.fetchBalances();
};

//prevent body scrolling whilst hamburger active
watch(
  () => hamburgerMenuState?.active,
  (hamburgNew) => {
    if (hamburgNew) lockBodyScroll();
    else unlockBodyScroll();
    if (hamburgNew) {
      $trackEvent('NavigationEvent', 'OPEN_HAMBURGER_MENU');
      if (!route.params?.game) refreshBalances();
    }
  },
);

const enableMenuText = computed(
  () => settings.getFeatureFlags.get('website.enablemenutext') || false,
);

function openSearch() {
  hamburgerMenuState.active = false;
  settings.activateModal('search');
}

const visible = defineModel('visible', { type: Boolean });

const emits = defineEmits<{
  (e: 'activatorClick'): void;
}>();

const customContainerClasses = computed(
  () =>
    `bg-base-secondary ${viewport.isLessThan('tablet') ? 'rounded-t-2xl top-2 overflow-hidden' : ''}`,
);
</script>

<template>
  <div>
    <Drawer
      v-model:visible="visible"
      :custom-transition-class="props?.drawerTransition"
      :custom-container-class="customContainerClasses"
    >
      <template v-if="!props?.hideActivator" #activator>
        <Button
          :aria-label="$t('menu')"
          element-name="hamburger-menu-open"
          type="transparent"
          class="flex flex-col mr-1 xs:mr-2 md:mr-3 px-1 md:px-2"
          @click="emits('activatorClick')"
        >
          <div>
            <Bars3BottomLeftIcon />
          </div>
          <div v-if="enableMenuText">
            <p class="m-0 text-xs font-bold text-base-priority lowercase">
              {{ $t('menu') }}
            </p>
          </div>
        </Button>
      </template>
      <template #header>
        <HamburgerMenuHeader
          @toggle-menu="hamburgerMenuState.active = !hamburgerMenuState.active"
        />
      </template>

      <div class="p-4">
        <div
          element-name="nav-bar-search"
          class="w-full flex cursor-pointer stacked-border rounded-lg bg-layer text-base whitespace-nowrap px-2 mb-4"
          @click="openSearch()"
        >
          <div class="flex stacked-border-color border-0 border-r mr-2">
            <ExternalIcon
              class="w-4 h-4 mr-2 self-center"
              icon-name="search-magnifying"
            />
          </div>
          <span class="py-2 text-xs text-base-priority">
            {{ $t('search-games') }}
          </span>
        </div>
        <HamburgerShortcuts
          @toggle-hamburger="
            hamburgerMenuState.active = !hamburgerMenuState.active
          "
        />
        <div class="flex flex-col gap-2">
          <DesignSystemAccordion
            v-if="auth.isLoggedIn"
            title="my-account"
            :classes="{
              title: 'text-xs !font-bold',
              header: 'bg-layer',
              icon: '!w-4 !h-4',
              content: 'bg-layer rounded-b-lg !px-0 !pb-0',
            }"
            size="lg"
          >
            <div class="flex flex-col">
              <div
                v-for="item in accountNavBarMenu"
                :key="item.id"
                class="w-full font-normal text-xs hover:bg-light-100 hover:dark:bg-dark-700 cursor-pointer text-base-priority p-2 px-4 capitalize"
                @click="[
                  auth.setAccountModalPage(item.id as AccountModals),
                  (hamburgerMenuState.active = false),
                ]"
              >
                {{ $t(item.key) }}
              </div>
            </div>
          </DesignSystemAccordion>
          <DesignSystemAccordion
            title="quick-links"
            :classes="{
              root: 'mb-3',
              title: 'text-xs !font-bold',
              header: 'bg-layer',
              icon: '!w-4 !h-4',
              content: 'bg-layer rounded-b-lg !px-0 !pb-0',
            }"
            size="lg"
          >
            <div class="flex flex-col">
              <NuxtLink
                v-for="item in settings.getNav({ nav: 'left' })"
                :key="item.Alias"
                :to="item.Path"
                :class="{
                  'dark:bg-dark-900 bg-light-300': activeRoute === item.Path,
                }"
                class="w-full font-normal text-xs hover:bg-light-100 hover:dark:bg-dark-700 cursor-pointer text-base-priority p-2 px-4"
              >
                <span
                  class="truncate"
                  @click="hamburgerMenuState.active = false"
                >
                  {{ $t(item.Alias) }}
                </span>
              </NuxtLink>
            </div>
          </DesignSystemAccordion>
        </div>

        <div>
          <p
            class="text-base-priority font-bold capitalize mb-2 text-center text-sm"
          >
            {{ $t('game-categories') }}
          </p>
          <HamburgerGameCategories
            @toggle-hamburger="hamburgerMenuState.active = false"
          />
        </div>
      </div>
      <template #footer>
        <div class="flex flex-col bg-layer p-2 gap-2 bottom-0 w-full">
          <UserInterfaceAppsDownload
            v-if="$displayApps"
            :direction="
              viewport.isLessThan('tablet') ? 'horizontal' : 'vertical'
            "
          />
          <GenericLanguageSwitcher />
        </div>
      </template>
    </Drawer>
  </div>
</template>
